import cookie from "js-cookie";
import once from "lodash.once";

import { identifyVisitor } from "lib/api";
import { Logger } from "lib/logger";
import { getUser } from "lib/user";

export const STORAGE_KEY = "fingerprint";
export const LONG_TERM_STORAGE_KEY = "fingerprint-lt";

const log = Logger("lib/fingerprint");

import * as FingerprintJS from "@fingerprintjs/fingerprintjs-pro";
import type { SessionUserTypeEnum } from "@shared/graphql.types";

const initFingerprint = (): Promise<FingerprintJS.Agent> => {
  if (!process.env.NEXT_PUBLIC_FINGERPRINT_API) {
    throw new Error(`Fingerprint API key missing`);
  }

  const baseProxyUrl =
    process.env.NEXT_PUBLIC_FINGERPRINT_CF_BASE_URI ??
    "https://www.beautypie.com";
  return FingerprintJS.load({
    apiKey: process.env.NEXT_PUBLIC_FINGERPRINT_API,
    scriptUrlPattern: [
      `${baseProxyUrl}/ghpw3b6spbisfiuvaavc/ycwjrqfdmfinlvcrfe66?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`,
      FingerprintJS.defaultScriptUrlPattern // Fallback to default CDN in case of error
    ],
    endpoint: [
      `${baseProxyUrl}/ghpw3b6spbisfiuvaavc/k8bemdonceuyuvrterq2?region=eu`,
      FingerprintJS.defaultEndpoint // Fallback to default endpoint in case of error
    ]
  });
};

type FingerprintCookie = {
  userId: string;
  userType?: SessionUserTypeEnum;
  fingerprintId?: string;
};

export const fingerprintUser = async (
  fingerprintJSAgent: FingerprintJS.Agent
): Promise<FingerprintCookie> => {
  const user = getUser();

  let userId: string | undefined;
  if (user?.id?.length && /^\d+$/.test(user?.id || "")) {
    userId = user?.id;
  }

  const fingerprintData = await fingerprintJSAgent
    ?.get({
      linkedId: userId
    })
    .catch((err) => {
      console.error(err);
      cookie.set(STORAGE_KEY, JSON.stringify({ userId: user.id }), {
        expires: 2
      });
    });

  if (fingerprintData?.sealedResult?.length) {
    try {
      const { bpFingerprintId, userType } = await identifyVisitor(
        "web",
        fingerprintData.sealedResult
      );
      if (!bpFingerprintId?.length) {
        throw new Error("Invalid fingerprint Id");
      }

      return setFingerprintCookie({
        fingerprintId: bpFingerprintId,
        userId: user.id,
        userType: userType ? userType : undefined
      });
    } catch (err) {
      log.error({ err }, "Error identifying visitor");
      cookie.set(
        STORAGE_KEY,
        JSON.stringify({ userId: user.id, userType: "new" }),
        {
          expires: 2
        }
      );
    }
  }

  return setFingerprintCookie({
    userId: user.id
  });
};

const fingerprint = () =>
  initFingerprint()
    .then(fingerprintUser)
    .catch((err) => {
      log.error(err);
      return undefined;
    });

export const setFingerprintCookie = (
  item: FingerprintCookie
): FingerprintCookie => {
  cookie.set(STORAGE_KEY, JSON.stringify(item), { expires: 2 });
  cookie.set(LONG_TERM_STORAGE_KEY, JSON.stringify(item), { expires: 180 });
  return item;
};

// Calling this function directly to get the fingerprint cookie may be violating user consent
// Please use `getFingerprint` unless you really need this
export const unsafeGetFingerprintCookie = (): FingerprintCookie | undefined => {
  const item = cookie.get(LONG_TERM_STORAGE_KEY);

  if (item) {
    try {
      const fingerprintCookie = JSON.parse(item);
      return fingerprintCookie;
    } catch (error) {
      console.error(error);
    }
  }
};

export const getBPFingerprint = once(async () => {
  if (!cookie.get(STORAGE_KEY)) {
    await fingerprint();
  }

  const userFingerprint = unsafeGetFingerprintCookie();

  if (userFingerprint?.fingerprintId) {
    return userFingerprint;
  }

  return await fingerprint();
});

export const clearBPFingerprint = () => {
  cookie.remove(LONG_TERM_STORAGE_KEY);
};
